import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, ChevronLeft, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { setJournalSEO, setNotFoundSEO } from '@/utils/seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JournalPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  // Set SEO when post loads (or not-found SEO when missing)
  useEffect(() => {
    if (post) {
      setJournalSEO(post);
    } else if (!loading) {
      setNotFoundSEO('Article Not Found');
    }
  }, [post, loading]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/journal/${slug}`);
      setPost(response.data.post);
      setRelated(response.data.related || []);
    } catch (error) {
      console.error('Error fetching post:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-32 pb-24 chaleur-container">
        <div className="max-w-3xl mx-auto animate-pulse">
          <div className="h-8 bg-secondary rounded w-3/4 mb-4" />
          <div className="aspect-video bg-secondary mb-8" />
          <div className="space-y-4">
            <div className="h-4 bg-secondary rounded" />
            <div className="h-4 bg-secondary rounded w-5/6" />
            <div className="h-4 bg-secondary rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="pt-32 pb-24 chaleur-container text-center" data-testid="journal-not-found">
        <h1 className="text-2xl mb-4">Article not found</h1>
        <p className="text-muted-foreground mb-6">This article may have been moved or is no longer available.</p>
        <Button asChild>
          <Link to="/journal">Back to Journal</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24" data-testid="journal-post-page">
      <article className="chaleur-container">
        <div className="max-w-3xl mx-auto">
          {/* Back Link */}
          <Link to="/journal" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to Journal
          </Link>

          {/* Header */}
          <header className="mb-8">
            <p className="caption text-primary mb-4">{post.category}</p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-light mb-6">{post.title}</h1>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" /> {post.author}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" /> {new Date(post.created_at).toLocaleDateString('en-US', { 
                  year: 'numeric', month: 'long', day: 'numeric' 
                })}
              </span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="aspect-video overflow-hidden mb-12">
            <img 
              src={post.featured_image || 'https://images.unsplash.com/photo-1606681246653-74521ac452f4?w=1200&q=80'}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">{post.excerpt}</p>
            <div dangerouslySetInnerHTML={{ __html: post.content }} />
          </div>

          {/* Tags */}
          {post.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t border-border">
              {post.tags.map(tag => (
                <span key={tag} className="px-3 py-1 bg-secondary text-sm">{tag}</span>
              ))}
            </div>
          )}

          {/* Share */}
          <div className="flex items-center gap-4 mt-8 pt-8 border-t border-border">
            <span className="text-sm text-muted-foreground">Share this article:</span>
            <button className="p-2 hover:text-primary transition-colors" aria-label="Share">
              <Share2 className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Related Posts */}
        {related.length > 0 && (
          <div className="max-w-5xl mx-auto mt-24">
            <h2 className="text-2xl font-light mb-8">Related Articles</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {related.map(p => (
                <Link key={p.id} to={`/journal/${p.slug}`} className="group">
                  <div className="aspect-[4/3] overflow-hidden mb-4">
                    <img 
                      src={p.featured_image || 'https://images.unsplash.com/photo-1606681246653-74521ac452f4?w=400&q=80'}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <p className="caption text-primary mb-2">{p.category}</p>
                  <h3 className="text-lg font-light group-hover:text-primary transition-colors">{p.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
};

export default JournalPostPage;
